import React, { useContext, useEffect, useRef } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { Link, useHistory, useLocation } from "react-router-dom";
import { timeDay } from 'd3-time';
import { Trans, useTranslation } from 'react-i18next';

import ConditionalLink from './ConditionalLink';
import DayFilterContext from '../DayFilterContext';
import NotificationBell from './NotificationBell';
import NotificationsContext from '../NotificationsContext';
import PresentationContext from '../PresentationContext';
import { demoOnly, getView } from '../Routes';
import { isMocked } from '../actions/authentication';
import { locales, reports } from '../utils/config';
import { parseDate, urlDateFormat } from '../utils/formats';
// import bell from '../images/bell.svg';
import './Dashboard.css';
import ErrorBoundary from '../ErrorBoundary';


function disable(event) {
  event.preventDefault();
}

// const enabledReports = reports.filter(r => r.id === 'agp' || r.id === 'day');
const enabledReports = reports;

function Header({ location: propsLocation, interactive = true, user: mockUser }) {
  const reduxStore = useSelector(state => state, shallowEqual);
  const { day, days = [] } = useContext(DayFilterContext);
  const { notifications = [], setNotifications } = useContext(NotificationsContext);

  const { locale, setLocale, weekInterval } = useContext(PresentationContext);
  const { t } = useTranslation('dashboard');
  const history = useHistory();
  const location = useLocation();

  const { session } = reduxStore;
  const { user } = session;
  const { access = {} } = user || {};
  const { consent, organization, practitioner = {} } = access;

  let targetDays = days;
  if (day && (!days.length || (days.indexOf(day) < 0))) {
    const d = parseDate(day);
    const startOfWeek = weekInterval.offset(weekInterval(d), (!days.length || (d < parseDate(days[0]))) ? -1 : 0);
    targetDays = `${urlDateFormat(startOfWeek)}_${urlDateFormat(timeDay.offset(startOfWeek, 13))}`;
  } else if (timeDay.count(parseDate(days[0]), parseDate(days[days.length - 1])) === (days.length - 1)) {
    // This can be shortened
    targetDays = `${days[0]}_${days[days.length - 1]}`;
  }
  const targetDay = day || days[0];
  const readOnly = (consent || organization || practitioner.id || practitioner.name);
  const settingsEnabled = !!user;
  const notificationsEnabled = !!user && !readOnly;
  const shownLocales = Object.keys(locales).map(k => ({
    name: k,
    description: locales[k],
  }));

  const userName = practitioner.name || (user && user.name) || '';

  const accountMenuLinks = [
    'account',
    'sources',
    'sharing',
  ];

  // TODO: inspect this by the class of the notification (permissions or missing data)
  const unread = notifications.some(n => n.read === false);

  const userMenuVisible = location.hash === '#userMenu';
  const userMenuLinkTarget = userMenuVisible ? '#' : '#userMenu';

  const notificationsVisible = location.hash === '#notifications';
  const notificationsLinkTarget = notificationsVisible ? '#' : '#notifications';

  const documentClickHandler = useRef();

  useEffect(() => {
    documentClickHandler.current = (event) => {
      let id = event.target.id;
      if (!id) {
        try {
          id = event.target.ownerSVGElement.parentNode.parentNode.id;
        } catch { }
      }
      switch (id) {
        case 'accountMenuButton':
        case 'accountHeading':
        case 'notificationsMenuButton':
        case 'notificationsHeading':
          break;
        default:
          if (userMenuVisible || notificationsVisible) {
            history.replace({ ...location, hash: '' });
          }
      }
    }
  }, [history, location, notificationsVisible, userMenuVisible]);

  useEffect(() => {
    const handler = event => documentClickHandler.current(event);
    document.addEventListener('click', handler, true);
    return () => {
      document.removeEventListener('click', handler, true);
    };
  });

  return (
    <ErrorBoundary>
      <header className="report">
        {
          enabledReports.map(r => (
            <ConditionalLink
              key={r.id}
              active={interactive}
              className={interactive ? '' : 'disabled'}
              onClick={interactive ? undefined : disable}
              to={`../${r.id}/${(r.id === 'day') ? targetDay : targetDays}`}
              disabled={!interactive}
              isActive={(m, myLocation) => getView(propsLocation || myLocation.pathname) === r.id}
            >
              <h1><Trans i18nKey={`dashboard:header.reports.${r.id}`} children={<b />} /></h1>
            </ConditionalLink>
          ))
        }
        <nav>
          <ul>
            {notificationsEnabled
              ? (
                <li
                  className={`${
                    notificationsVisible ? 'active' : ''
                  } ${
                    unread ? 'unread' : ''
                  }`.trim()}
                  role="menuitem"
                >
                  <ConditionalLink
                    id="notificationsMenuButton"
                    active={interactive}
                    className={interactive ? '' : 'disabled'}
                    onClick={interactive ? undefined : disable}
                    to={notificationsLinkTarget}
                    disabled={!interactive}
                    role="button"
                    aria-haspopup="true"
                    aria-controls="notifications"
                    aria-expanded={notificationsVisible}
                    isActive={() => notificationsVisible}
                  >
                    <NotificationBell notifications={notifications.length} unreads={unread} />
                  </ConditionalLink>
                  <ul
                    id="notifications"
                    className={`menu ${notificationsVisible ? 'visible' : 'hidden'}`}
                    role="menu"
                    aria-labelledby="notificationsHeading"
                  >
                    <li id="notificationsHeading" className="heading">
                      {t('header.notificationsMenu.notifications')}
                    </li>
                    {notifications.length
                      ? (
                        notifications.map((n, i) => (
                          <li key={n.id || i} role="menuitem">
                            <a
                              href={n.href || '#'}
                              onClick={(event) => {
                                event.preventDefault();
                                n.read = true;
                                // TODO: check that 'n' was modified here!
                                setNotifications(notifications);
                                n.action(event);
                              }}
                            >
                              {n.prompt}
                            </a>
                          </li>
                        ))
                      )
                      : (
                        <li className="empty">
                          <p>
                            {t('header.notificationsMenu.empty')}
                          </p>
                        </li>
                      )}
                  </ul>
                </li>
              )
              : undefined
            }
            {settingsEnabled
              ? (
                <li className={userMenuVisible ? 'active' : ''} role="menuitem">
                  <ConditionalLink
                    id="accountMenuButton"
                    active={interactive}
                    className={interactive ? '' : 'disabled'}
                    onClick={interactive ? undefined : disable}
                    to={userMenuLinkTarget}
                    disabled={!interactive}
                    role="button"
                    aria-haspopup="true"
                    aria-controls="accountMenu"
                    aria-expanded={userMenuVisible}
                    isActive={() => userMenuVisible}
                  >
                    {(mockUser ? mockUser.name : user.name) || t('header.accountMenu.settings')}
                    <span className="dropdownIcon" aria-hidden> ▼</span>
                  </ConditionalLink>
                  <ul
                    id="accountMenu"
                    className={`menu ${userMenuVisible ? 'visible' : 'hidden'}`}
                    role="menu"
                    aria-labelledby="accountHeading"
                  >
                    <li id="accountHeading" className="heading">
                      {t('header.accountMenu.settings')}
                    </li>
                    {(!interactive || demoOnly || readOnly || isMocked(user))
                      ? shownLocales.map((l) => (
                        <li key={l.name} role="menuitem">
                          <ConditionalLink
                            active={interactive && (navigator.userAgent !== 'ReactSnap')}
                            isActive={() => (locale === l.name)}
                            to={`?lng=${l.name}`}
                            onClick={(event) => {
                              event.preventDefault();
                              setLocale(l.name);
                            }}
                          >
                            {l.description}
                          </ConditionalLink>
                        </li>
                      ))
                      : accountMenuLinks.map(l => (
                        <li id={l} key={l}>
                          <Link to={`/${l}`}>{t(`header.accountMenu.${l}`)}</Link>
                        </li>
                      ))
                    }
                    {!isMocked(user)
                      ? (
                        <li id="logoutLink" key="logout">
                          <Link to="/logout/">{t(`header.accountMenu.logout`)}</Link>
                        </li>
                      )
                      : undefined
                    }
                  </ul>
                </li>
              )
              : userName
            }
          </ul>
        </nav>
        <fieldset>
          <label>
            <span>{t('header.name')}</span>
            <input type="text" className="name" defaultValue={(user && user.name)} />
          </label>
          <label>
            <span>{t('header.MRN')}</span>
            <input type="text" className="mrn" defaultValue={user ? user.MRN : undefined} />
          </label>
        </fieldset>
      </header>
    </ErrorBoundary>
  );
}

export default Header;
